import { useState, useCallback } from 'react';
import { Heart } from 'lucide-react';
import { useFavorites } from '@/context/FavoritesContext';
import { useToast } from '@/context/ToastContext';
import type { FavoriteButtonProps } from '@/types/favorites';

/**
 * A reusable button component for favoriting/unfavoriting items.
 * Shows an outline heart when not favorited, filled heart when favorited.
 */
export function FavoriteButton({
  itemId,
  itemType,
  cachedData,
  size = 'md',
  className = '',
}: FavoriteButtonProps) {
  const { isFavorited, toggleFavorite } = useFavorites();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const favorited = isFavorited(itemId, itemType);

  // Size mappings
  const sizeMap = {
    sm: 14,
    md: 18,
    lg: 22,
  };

  const paddingMap = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2',
  };

  const iconSize = sizeMap[size];
  const padding = paddingMap[size];

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isLoading) return;

      setIsLoading(true);
      setIsAnimating(true);

      try {
        const result = await toggleFavorite(itemId, itemType, cachedData);
        showToast({
          message: result.favorited
            ? `Added "${cachedData.title}" to favorites`
            : `Removed "${cachedData.title}" from favorites`,
          type: 'success',
          duration: 2000,
        });
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
        showToast({
          message: 'Failed to update favorites',
          type: 'error',
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
        // Keep animation state for a bit longer for visual feedback
        setTimeout(() => setIsAnimating(false), 300);
      }
    },
    [itemId, itemType, cachedData, isLoading, toggleFavorite, showToast]
  );

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`
        ${padding}
        rounded-full
        transition-all
        duration-200
        ${
          favorited
            ? 'text-red-500 hover:text-red-600 hover:bg-red-50'
            : 'text-slate-400 hover:text-red-400 hover:bg-red-50'
        }
        ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer active:scale-90'}
        ${isAnimating ? 'scale-125' : 'scale-100'}
        ${className}
      `}
      title={favorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Heart
        size={iconSize}
        fill={favorited ? 'currentColor' : 'none'}
        className={`
          transition-all
          duration-200
          ${isAnimating ? 'animate-pulse' : ''}
        `}
      />
    </button>
  );
}

/**
 * A compact inline favorite button for use in lists
 */
export function FavoriteButtonInline({
  itemId,
  itemType,
  cachedData,
  className = '',
}: Omit<FavoriteButtonProps, 'size'>) {
  return (
    <FavoriteButton
      itemId={itemId}
      itemType={itemType}
      cachedData={cachedData}
      size="sm"
      className={className}
    />
  );
}

/**
 * A larger favorite button for detail views
 */
export function FavoriteButtonLarge({
  itemId,
  itemType,
  cachedData,
  className = '',
}: Omit<FavoriteButtonProps, 'size'>) {
  return (
    <FavoriteButton
      itemId={itemId}
      itemType={itemType}
      cachedData={cachedData}
      size="lg"
      className={className}
    />
  );
}
